"use client";

import { useRef, useEffect, ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";

interface CustomDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  excludeRefs?: Array<React.RefObject<HTMLElement | null>>;
  useHeight?: boolean; // New prop to control whether to use height or scale
}

export default function CustomDropdown({ isOpen, onClose, children, className, excludeRefs = [], useHeight = false }: CustomDropdownProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && dropdownRef.current.contains(event.target as Node)) return;

      for (const ref of excludeRefs) {
        if (ref.current && ref.current.contains(event.target as Node)) return;
      }

      onClose();
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, excludeRefs]);

  const motionProps = useHeight
    ? {
        initial: { opacity: 0, height: 0, y: -10 },
        animate: { opacity: 1, height: "auto", y: 0 },
        exit: { opacity: 0, height: 0, y: -10 },
      }
    : {
        initial: { opacity: 0, scaleX: 0.95, y: -10 },
        animate: { opacity: 1, scaleX: 1, y: 0 },
        exit: { opacity: 0, scaleX: 0.98, y: 7 },
      };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div ref={dropdownRef} {...motionProps} transition={{ duration: 0.3 }} className={`absolute bg-white border border-gray-200 ${className || ""}`}>
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
